// Pure nav search for the office global search (Session AE, Item 2). Matches the typed text against
// every office nav item's label, HINT and route path (case-insensitive substring), so "sub" finds
// Subcontractors, "loler" finds LOLER, "def" finds Defects. Instant (no API call) and unit-testable.
//
// THE HINT IS INDEXED (Session ALU), and that is load-bearing rather than a nicety. A rename takes
// the old word out of the index, and this repo renames nav items regularly: after ALU, "service
// schedule" and "engineer compliance" are both words the office has typed for months that are now
// nobody's label, and neither appears in the page's PATH either (/office/ppm, /office/personnel),
// so the existing path fallback -- which is what still rescues "ppm" and "nps" -- could not catch
// them. Indexing the hint means a retired name can be kept findable by writing it into the hint
// the user already sees on hover, in ONE place, instead of maintaining a synonyms table beside
// the nav. Ranked BELOW label matches so an exact label still wins the top slot.
import { OFFICE_NAV, visibleNavForRole, officeNavEntries, type NavItem } from "./nav";
import type { Role } from "./api";
import { SETTINGS_SECTIONS } from "./settingsSections";

export interface NavSearchResult extends NavItem {
  section: string; // the group the item lives in (shown as a hint in the dropdown)
  // SC7A: for a page inside a hub (a tab), the label of the top-level item that holds it, so the
  // dropdown can say "Sites, in Customers". The menu now holds 14 hubs and every other page is a tab,
  // so search is how a person who does not know which hub holds a page finds it.
  hub?: string;
}

const NAV_RESULT_LIMIT = 6;

// SC7B: Settings is one page of sections, one on screen at a time, each with its own address. The menu holds
// only the Settings item, so the sections are searchable HERE (by their label and by the names of the cards
// inside them, old and new: "signature", "postcode cache", "chase"), and a hit opens the right section. They
// follow the other entries in each tier, so a page's own name still wins. Present only when Settings is in the menu.
function settingsSectionEntries(sections: ReturnType<typeof visibleNavForRole>, role?: Role): NavSearchResult[] {
  const group = sections.find((s) => s.items.some((i) => i.to === "/office/settings"));
  if (!group) return [];
  // Role-aware (SC7B review): the words of an admin-only card are searchable only by an admin. Any other role that is given
  // (office, or one this code does not know) is treated as not admin, the same fail-closed rule as visibleNavForRole; an omitted
  // role searches everything, as the rest of this file does.
  const withAdminCards = role === undefined || role === "admin";
  return SETTINGS_SECTIONS.map((s) => ({
    to: `/office/settings/${s.id}`,
    // Labelled "Settings: Email" so it never reads as (or collides with) a page of the same name, such as Subcontractors.
    label: `Settings: ${s.label}`,
    icon: "SE",
    hint: withAdminCards && s.adminHint ? `${s.hint}; ${s.adminHint}` : s.hint,
    section: group.label,
    hub: "Settings",
  }));
}

// Matching nav items (with their section), label match ranked before hint match before path-only
// match, capped. Each item appears at most once (the first tier it matches). An empty / whitespace
// query returns nothing. Pure.
//
// P-1, Session AQT: `role` is filtered through visibleNavForRole first, so an admin-only item
// (its own page already refuses a non-admin) cannot be found and clicked into a dead end from
// search either -- the same reason it is hidden from the menu itself. Omitted role searches
// everything, matching the pre-P-1 behaviour for any caller that has not been updated yet.
// `role` is deliberately the LAST parameter, after the pre-existing `limit`, so every existing
// two-argument call (query, limit) keeps working unchanged.
export function searchNav(query: string, limit = NAV_RESULT_LIMIT, role?: Role): NavSearchResult[] {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  const labelHits: NavSearchResult[] = [];
  const hintHits: NavSearchResult[] = [];
  const pathHits: NavSearchResult[] = [];
  const sections = role ? visibleNavForRole(OFFICE_NAV, role) : OFFICE_NAV;
  // Every top-level item AND every tab (the first tab of a hub is its own page, searchable by its own
  // label: "To fix", "Calendar"). A page reachable two ways (the hub item and its first tab share a
  // path) is listed once, at its best tier.
  const seen = new Set<string>();
  const place = (entry: NavSearchResult, hit: "label" | "hint" | "path") => {
    const key = entry.to;
    if (hit === "label") labelHits.push(entry);
    else if (hit === "hint") hintHits.push(entry);
    else pathHits.push(entry);
    seen.add(key);
  };
  const entries = [...(officeNavEntries(sections, true) as NavSearchResult[]), ...settingsSectionEntries(sections, role)];
  // Pass 1 label hits, pass 2 hint hits, pass 3 path hits, each skipping a path already listed at a better tier.
  for (const e of entries) {
    if (!seen.has(e.to) && e.label.toLowerCase().includes(q)) place(e, "label");
  }
  for (const e of entries) {
    if (!seen.has(e.to) && e.hint && e.hint.toLowerCase().includes(q)) place(e, "hint");
  }
  for (const e of entries) {
    if (!seen.has(e.to) && e.to.toLowerCase().includes(q)) place(e, "path");
  }
  return [...labelHits, ...hintHits, ...pathHits].slice(0, limit);
}
